import { RefreshCw, Tablet } from 'lucide-react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTranslation } from '@/hooks/useTranslation';
import { activateDevice } from '@/services/api.client';

/**
 * DevicePairingPage — shown on a tablet when no device session exists.
 * The PM generates a 6-digit code from their dashboard, and enters it here.
 *
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5
 */
export function DevicePairingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const mutation = useMutation({
    mutationFn: () => activateDevice(digits.join('')),
    onSuccess: () => {
      // Invalidate the 'me' query so AuthContext re-fetches using the new
      // device session token (getGuardDeviceMe instead of getMe). Without this,
      // the cached failed /me response would cause GuardOrDeviceRoute to
      // redirect to /login when the guard navigates to /guard after shift select.
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      void navigate('/guard/select');
    },
    onError: (err: { message?: string; status?: number }) => {
      if (err.status === 429) {
        setError(t('guard.pairing.tooManyAttempts'));
      } else {
        setError(err.message ?? t('guard.pairing.invalidCode'));
      }
      // Clear digits on error
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    },
  });

  const handleDigitChange = (index: number, value: string) => {
    // Only allow single digits
    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);
    setError(null);

    // Auto-advance to next input
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (digit && index === 5 && newDigits.every((d) => d !== '')) {
      mutation.mutate();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newDigits = pasted.split('');
      setDigits(newDigits);
      setError(null);
      inputRefs.current[5]?.focus();
      // Auto-submit
      setTimeout(() => mutation.mutate(), 100);
    }
  };

  const handleSubmit = () => {
    if (digits.every((d) => d !== '')) {
      mutation.mutate();
    }
  };

  const isComplete = digits.every((d) => d !== '');

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--bg-base)] p-4">
      <div className="w-full max-w-sm space-y-8 text-center">
        {/* Icon and title */}
        <div className="flex flex-col items-center gap-4">
          <div className="flex size-16 items-center justify-center rounded-full bg-[var(--cta-primary-bg)]/10">
            <Tablet className="size-8 text-[var(--cta-primary-bg)]" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-[var(--text-primary)]">
              {t('guard.pairing.title')}
            </h1>
            <p className="mt-2 text-sm text-[var(--text-secondary)]">
              {t('guard.pairing.description')}
            </p>
          </div>
        </div>

        {/* 6-digit code input */}
        <div className="flex justify-center gap-2" onPaste={handlePaste}>
          {digits.map((digit, i) => (
            <Input
              key={i}
              ref={(el) => {
                inputRefs.current[i] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleDigitChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              className="size-14 text-center text-2xl font-mono border-2 rounded-lg focus:border-[var(--cta-primary-bg)]"
              aria-label={t('guard.pairing.digitAria').replace('{{number}}', String(i + 1))}
              disabled={mutation.isPending}
              autoFocus={i === 0}
            />
          ))}
        </div>

        {/* Error message */}
        {error && (
          <p className="text-sm text-[var(--text-error)]" role="alert">
            {error}
          </p>
        )}

        {/* Submit button */}
        <Button
          className="w-full min-h-[48px] text-base"
          onClick={handleSubmit}
          disabled={!isComplete || mutation.isPending}
        >
          {mutation.isPending && <RefreshCw className="mr-2 size-4 animate-spin" />}
          {mutation.isPending ? t('guard.pairing.activating') : t('guard.pairing.activateButton')}
        </Button>

        {/* Help text */}
        <p className="text-xs text-[var(--text-tertiary)]">{t('guard.pairing.helpText')}</p>
      </div>
    </div>
  );
}
